"use client"

import * as React from "react"
import { log } from '@/lib/logger'
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { MessageSquare, Brain, Loader2, CheckCircle, AlertCircle } from "lucide-react"
import type { Mail } from "@/lib/data"
import { conversationsApi } from "@/services/conversationsApi"

type ConversationFeedbackResponse = {
  results?: Array<{ comment?: string; feedback?: string }>
  feedback?: string
}

type PhoneCallFeedbackResponse = {
  feedback?: string
}

interface FeedbackFormProps {
  mail: Mail | null
}

const FeedbackFormComponent = ({ mail }: FeedbackFormProps) => {
  const [conversationFeedback, setConversationFeedback] = React.useState("")
  const [generalFeedback, setGeneralFeedback] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)
  const [loadingExistingFeedback, setLoadingExistingFeedback] = React.useState(false)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Initialize conversation feedback from server (fallback to mail prop)
  React.useEffect(() => {
    let isMounted = true

    const loadFeedback = async () => {
      if (!mail?.id) {
        setConversationFeedback("")
        return
      }

      // Optimistically show whatever we already have while loading
      setConversationFeedback(mail.feedback || "")
      setLoadingExistingFeedback(true)
      setError(null)

      try {
        if (mail.platform === "phone_call") {
          const data = (await conversationsApi.getPhoneCallFeedback(mail.id)) as PhoneCallFeedbackResponse
          if (!isMounted) return
          setConversationFeedback(data?.feedback || "")
        } else {
          const data = (await conversationsApi.getConversationFeedback(mail.id, { limit: 1 })) as ConversationFeedbackResponse
          const latest =
            data?.results?.[0]?.comment ||
            data?.results?.[0]?.feedback ||
            data?.feedback ||
            mail.feedback ||
            ""

          if (!isMounted) return
          setConversationFeedback(latest)
        }
      } catch {
        // Keep whatever was already shown; surface a friendly error
        if (!isMounted) return
        setError("No se pudo cargar el feedback guardado. Intenta nuevamente.")
      } finally {
        if (isMounted) {
          setLoadingExistingFeedback(false)
        }
      }
    }

    loadFeedback()

    return () => {
      isMounted = false
    }
  }, [mail?.id, mail?.platform, mail?.feedback])

  // Clear success message after 5 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  const handleConversationFeedback = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!conversationFeedback.trim() || !mail?.id || submitting || loadingExistingFeedback) return
    
    setSubmitting(true)
    setError(null)
    
    try {
      // Check if this is a phone call
      log.debug('🔍 Feedback Debug:', {
        mailId: mail?.id,
        platform: mail?.platform,
        isPhoneCall: mail?.platform === 'phone_call'
      })
      
      if (mail?.platform === 'phone_call') {
        log.debug('📞 Using phone call feedback endpoint')
        // Use phone call feedback endpoint
        await conversationsApi.updatePhoneCallFeedback(mail.id, conversationFeedback.trim())
      } else {
        log.debug('💬 Using regular conversation feedback endpoint')
        // Use structured conversation feedback endpoint
        await conversationsApi.createConversationFeedback(mail.id, { comment: conversationFeedback.trim() })
      }
      
      setConversationFeedback(conversationFeedback.trim())
      setSuccessMessage("Feedback de conversación guardado correctamente")
    } catch (err: unknown) {
      
      // Extract meaningful error message
      let errorMessage = 'Error al guardar feedback'
      
      if (err && typeof err === 'object' && 'status' in err) {
        const error = err as { status: number; message?: string }
        if (error.status === 405) {
          errorMessage = 'Método no permitido. El servidor no acepta esta operación.'
        } else if (error.status === 404) {
          errorMessage = 'Conversación no encontrada. Verifica que la conversación existe.'
        } else if (error.status === 400) {
          errorMessage = 'Datos inválidos. Verifica el contenido del feedback.'
        } else if (error.status >= 500) {
          errorMessage = 'Error del servidor. Intenta nuevamente en unos momentos.'
        } else if (error.message) {
          errorMessage = error.message
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        const error = err as { message: string }
        errorMessage = error.message
      }
      
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleGeneralFeedback = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!generalFeedback.trim() || submitting) return
    
    setSubmitting(true)
    setError(null)
    
    try {
      await conversationsApi.submitGeneralFeedback(generalFeedback.trim())
      
      setGeneralFeedback("")
      setSuccessMessage("Feedback general enviado correctamente")
    } catch (err: unknown) {
      
      // Extract meaningful error message
      let errorMessage = 'Error al enviar feedback general'
      
      if (err && typeof err === 'object' && 'status' in err) {
        const error = err as { status: number; message?: string }
        if (error.status === 400) {
          errorMessage = 'El feedback no puede estar vacío. Escribe tu sugerencia.'
        } else if (error.status >= 500) {
          errorMessage = 'Error del servidor. Intenta nuevamente en unos momentos.'
        } else if (error.message) {
          errorMessage = error.message
        }
      } else if (err && typeof err === 'object' && 'message' in err) {
        const error = err as { message: string }
        errorMessage = error.message
      }
      
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (!mail) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No conversation selected</h3>
          <p className="text-sm text-muted-foreground">Choose a conversation to provide feedback</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* Success Message */}
      {successMessage && (
        <Alert className="border-success/20 bg-success/10">
          <CheckCircle className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <div className="font-medium">Error al procesar el feedback</div>
            <div className="text-sm">{error}</div>
            <div className="text-xs text-muted-foreground">
              Si el problema persiste, contacta al administrador del sistema.
            </div>
            <Button 
              variant="outline" 
              size="xs"
              onClick={() => setError(null)}
              className="mt-2"
            >
              Cerrar error
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Conversation-specific Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Feedback para esta conversación
          </CardTitle>
          <CardDescription>
            Añade notas o instrucciones específicas sobre cómo manejar esta conversación
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleConversationFeedback} className="space-y-4">
            <div>
              <Textarea
                value={conversationFeedback}
                onChange={(e) => setConversationFeedback(e.target.value)}
                placeholder="Ej: 'Cliente interesado en productos premium', 'Usar tono formal', 'Priorizar información de precios'..."
                className="min-h-[100px]"
                disabled={submitting || loadingExistingFeedback}
              />
            </div>
            <Button 
              type="submit" 
              disabled={!conversationFeedback.trim() || submitting || loadingExistingFeedback}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar feedback de conversación"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* General System Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Feedback general del sistema
          </CardTitle>
          <CardDescription>
            Sugiere mejoras para el comportamiento general de la IA
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGeneralFeedback} className="space-y-4">
            <div>
              <Textarea
                value={generalFeedback}
                onChange={(e) => setGeneralFeedback(e.target.value)}
                placeholder="¿Qué podría mejorar la IA en general? ¿Nuevas funcionalidades? ¿Cambios en el comportamiento?"
                className="min-h-[100px]"
                disabled={submitting}
              />
            </div>
            <Button 
              type="submit" 
              disabled={!generalFeedback.trim() || submitting}
              variant="outline"
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar feedback general"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export const FeedbackForm = React.memo(FeedbackFormComponent)
